import { useRef, useState } from 'react'
import type { Rahmen } from '../api/typen'

interface Eigenschaften {
  bildUrl: string
  rahmen: Rahmen
  aendern: (rahmen: Rahmen) => void
}

type Modus = 'verschieben' | 'groesse' | null

/**
 * P2-03: Eingrenzung des gemeinten Moduls auf dem Gesamtfoto. Alle Werte sind Prozentangaben
 * bezogen auf das Bild, damit sie unabhaengig von der Aufloesung des Geraets bleiben.
 * Bedienung mit dem Finger: in den Rahmen tippen und ziehen verschiebt, die Ecke unten rechts
 * zieht die Groesse.
 */
export default function RahmenWaehler({ bildUrl, rahmen, aendern }: Eigenschaften) {
  const flaecheRef = useRef<HTMLDivElement>(null)
  const [modus, setModus] = useState<Modus>(null)

  const prozent = (ereignis: React.PointerEvent) => {
    const flaeche = flaecheRef.current?.getBoundingClientRect()
    if (!flaeche) return null
    return {
      x: ((ereignis.clientX - flaeche.left) / flaeche.width) * 100,
      y: ((ereignis.clientY - flaeche.top) / flaeche.height) * 100,
    }
  }

  const bewegen = (ereignis: React.PointerEvent) => {
    if (!modus) return
    const punkt = prozent(ereignis)
    if (!punkt) return

    if (modus === 'verschieben') {
      aendern({
        ...rahmen,
        x: begrenze(punkt.x - rahmen.breite / 2, 0, 100 - rahmen.breite),
        y: begrenze(punkt.y - rahmen.hoehe / 2, 0, 100 - rahmen.hoehe),
      })
    } else {
      aendern({
        ...rahmen,
        breite: begrenze(punkt.x - rahmen.x, 10, 100 - rahmen.x),
        hoehe: begrenze(punkt.y - rahmen.y, 10, 100 - rahmen.y),
      })
    }
  }

  return (
    <div
      ref={flaecheRef}
      style={{ position: 'relative', touchAction: 'none', userSelect: 'none' }}
      onPointerMove={bewegen}
      onPointerUp={() => setModus(null)}
      onPointerCancel={() => setModus(null)}
    >
      <img src={bildUrl} alt="Gesamtfoto des Schranks" style={{ width: '100%', borderRadius: 8 }} />

      <div
        onPointerDown={(ereignis) => {
          ereignis.currentTarget.setPointerCapture(ereignis.pointerId)
          setModus('verschieben')
        }}
        style={{
          position: 'absolute',
          left: `${rahmen.x}%`,
          top: `${rahmen.y}%`,
          width: `${rahmen.breite}%`,
          height: `${rahmen.hoehe}%`,
          border: '3px solid var(--gpi-cyan)',
          borderRadius: 4,
          boxShadow: '0 0 0 9999px rgba(18,27,76,0.45)',
          cursor: 'move',
        }}
      >
        <div
          onPointerDown={(ereignis) => {
            ereignis.stopPropagation()
            ereignis.currentTarget.setPointerCapture(ereignis.pointerId)
            setModus('groesse')
          }}
          style={{
            position: 'absolute',
            right: -14,
            bottom: -14,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--gpi-cyan)',
            border: '2px solid #fff',
            cursor: 'nwse-resize',
          }}
        />
      </div>
    </div>
  )
}

function begrenze(wert: number, minimum: number, maximum: number) {
  return Math.max(minimum, Math.min(maximum, wert))
}
